import React, { useState, useEffect, useRef } from 'react';
import {
    Check, Clock, Info, ChevronDown, ChevronUp, Briefcase,
    Building2, Globe, AlertTriangle, Eye, Loader2, ChevronRight, ChevronLeft,
    Plus, Calendar, FileText, Bell, MessageSquare, Trash2, MoreVertical,
    CheckSquare, Save, PenLine, Users
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CourseEditor = ({
    formData,
    setFormData,
    courseType,
    activeStep,
    setActiveStep,
    onSave,
    onCancel,
    isSaving,
    timelineItems: propTimelineItems,
    setTimelineItems: propSetTimelineItems,
    onEditActivity
}) => {
    const steps = ['details', 'timeline', 'enrollment', 'review'];
    const currentStepIndex = steps.indexOf(activeStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // Data for dropdowns
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        lifecycle: false,
        roles: false,
        companyWide: false
    });

    // Timeline state - use props if provided (from AI), otherwise use local state
    const [localTimelineItems, setLocalTimelineItems] = useState([]);
    const timelineItems = propTimelineItems !== undefined ? propTimelineItems : localTimelineItems;
    const setTimelineItems = propSetTimelineItems || setLocalTimelineItems;

    const [availableTasks, setAvailableTasks] = useState([]);
    const [availableNotices, setAvailableNotices] = useState([]);
    const [availableSurveys, setAvailableSurveys] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addItemType, setAddItemType] = useState('task');

    // Card menu state
    const [openMenuId, setOpenMenuId] = useState(null);
    const [changeDayItemId, setChangeDayItemId] = useState(null);
    const [changeDayValue, setChangeDayValue] = useState('');

    // Preview state
    const [previewCount, setPreviewCount] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewEmployees, setPreviewEmployees] = useState([]);

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const initialFormDataRef = useRef(null);
    const initialTimelineRef = useRef(null);

    // Store initial state on mount to detect changes
    useEffect(() => {
        if (initialFormDataRef.current === null) {
            initialFormDataRef.current = JSON.stringify(formData);
            initialTimelineRef.current = JSON.stringify(propTimelineItems || []);
        }
    }, []);

    // Detect changes to form data or timeline
    useEffect(() => {
        if (initialFormDataRef.current !== null) {
            const currentFormData = JSON.stringify(formData);
            const currentTimeline = JSON.stringify(propTimelineItems || []);
            const hasChanges = currentFormData !== initialFormDataRef.current ||
                currentTimeline !== initialTimelineRef.current;
            setHasUnsavedChanges(hasChanges);
        }
    }, [formData, propTimelineItems]);

    // Warn user before leaving page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Close kebab menu on outside click
    useEffect(() => {
        if (openMenuId) {
            const handleClickOutside = () => setOpenMenuId(null);
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openMenuId]);

    // Handle cancel with confirmation
    const handleCancelWithConfirmation = () => {
        if (hasUnsavedChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to leave? All changes will be lost.')) {
                onCancel();
            }
        } else {
            onCancel();
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchAvailableItems();
    }, []);

    useEffect(() => {
        if (formData.enrollment_method === 'automatic') {
            fetchPreview();
        }
    }, [formData.auto_enrollment_rules, formData.enrollment_method]);

    const fetchRoles = async () => {
        setLoadingRoles(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/employees/roles', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        } finally {
            setLoadingRoles(false);
        }
    };

    const fetchAvailableItems = async () => {
        setLoadingItems(true);
        try {
            const token = localStorage.getItem('token');

            const tasksRes = await api.get('/tasks', {
                headers: { Authorization: `Bearer ${token}` },
                params: { phase: courseType, status: 'active', limit: 100 }
            });
            setAvailableTasks(tasksRes.data?.tasks || []);

            const noticesRes = await api.get('/notices', {
                headers: { Authorization: `Bearer ${token}` },
                params: { status: 'active', limit: 100 }
            });
            setAvailableNotices(noticesRes.data?.notices || []);

            try {
                const surveysRes = await api.get('/surveys', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { status: 'active', limit: 100 }
                });
                setAvailableSurveys(surveysRes.data?.surveys || []);
            } catch {
                setAvailableSurveys([]);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        } finally {
            setLoadingItems(false);
        }
    };

    const fetchPreview = async () => {
        setPreviewLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/courses/preview-enrollments', {
                course_type: courseType,
                rules: formData.auto_enrollment_rules
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewCount(response.data.count || 0);
            setPreviewEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching preview:', error);
            setPreviewCount(0);
        } finally {
            setPreviewLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleLifecycleChange = (stage) => {
        const currentStages = formData.auto_enrollment_rules?.lifecycle_stages || [];
        const newStages = currentStages.includes(stage)
            ? currentStages.filter(s => s !== stage)
            : [...currentStages, stage];

        setFormData(prev => ({
            ...prev,
            auto_enrollment_rules: {
                ...prev.auto_enrollment_rules,
                lifecycle_stages: newStages
            }
        }));
    };

    const handleRoleToggle = (role) => {
        const currentRoles = formData.auto_enrollment_rules?.roles || [];
        const newRoles = currentRoles.includes(role)
            ? currentRoles.filter(r => r !== role)
            : [...currentRoles, role];

        setFormData(prev => ({
            ...prev,
            auto_enrollment_rules: {
                ...prev.auto_enrollment_rules,
                roles: newRoles
            }
        }));
    };

    const handleCompanyWide = (enabled) => {
        setFormData(prev => ({
            ...prev,
            auto_enrollment_rules: {
                ...prev.auto_enrollment_rules,
                company_wide: enabled,
                lifecycle_stages: enabled ? [] : prev.auto_enrollment_rules?.lifecycle_stages || [],
                roles: enabled ? [] : prev.auto_enrollment_rules?.roles || []
            }
        }));
    };

    const handleNextStep = () => {
        if (currentStepIndex < steps.length - 1) {
            setActiveStep(steps[currentStepIndex + 1]);
        }
    };

    const handlePrevStep = () => {
        if (currentStepIndex > 0) {
            setActiveStep(steps[currentStepIndex - 1]);
        }
    };

    // Timeline item handlers
    const handleAddTimelineItem = (item, type) => {
        const newItem = {
            id: `${type}_${item.id}_${Date.now()}`,
            item_id: item.id,
            item_type: type,
            name: item.title || item.name || item.subject,
            description: item.description,
            trigger_day: 0,
            order: timelineItems.length
        };
        setTimelineItems(prev => [...prev, newItem]);
        setShowAddModal(false);
        toast.success(`Added ${type} to timeline`);
    };

    const handleRemoveTimelineItem = (id) => {
        setTimelineItems(prev => prev.filter(item => item.id !== id));
    };

    const handleUpdateTriggerDay = (id, day) => {
        setTimelineItems(prev => prev.map(item =>
            item.id === id ? { ...item, trigger_day: parseInt(day) || 0 } : item
        ));
    };

    const hasAnyTriggers = (formData.auto_enrollment_rules?.lifecycle_stages?.length > 0) ||
        (formData.auto_enrollment_rules?.roles?.length > 0) ||
        formData.auto_enrollment_rules?.company_wide;

    const isPreboarding = courseType === 'preboarding';

    // Create a new blank inline task
    const handleCreateNewTask = (taskType = 'task') => {
        const newItem = {
            id: `new_task_${Date.now()}`,
            item_type: 'task',
            trigger_day: 0,
            order: timelineItems.length,
            task: {
                title: 'New Task',
                description: '',
                task_type: taskType,
                estimated_duration: 30,
                priority: 'medium',
                passing_score: taskType === 'quiz' ? 80 : null,
                content: getDefaultContent(taskType)
            },
            assignment_placeholders: []
        };
        setTimelineItems(prev => [...prev, newItem]);
        setShowAddModal(false);
        toast.success('New task created');
        if (onEditActivity) {
            setTimeout(() => onEditActivity(timelineItems.length), 0);
        }
    };

    const getDefaultContent = (taskType) => {
        switch (taskType) {
            case 'quiz':
                return {
                    questions: [{
                        id: 1, text: 'Sample question?', type: 'multiple_choice',
                        options: ['Option A', 'Option B', 'Option C', 'Option D'], correct_answer: 0
                    }]
                };
            case 'training':
                return { sections: [{ id: 1, title: 'Introduction', content: 'Add training content here...' }] };
            case 'checklist':
                return { items: [{ id: 1, text: 'First item', required: true, completed: false }] };
            default:
                return null;
        }
    };

    const handleCreateNewNotice = () => {
        const newItem = {
            id: `new_notice_${Date.now()}`,
            item_type: 'notice',
            trigger_day: 0,
            order: timelineItems.length,
            notice: {
                title: 'New Notice',
                content: '',
                priority: 'normal',
                show_popup_on_login: false,
                external_link: ''
            },
            assignment_placeholders: []
        };
        setTimelineItems(prev => [...prev, newItem]);
        setShowAddModal(false);
        toast.success('New notice created');
        if (onEditActivity) {
            setTimeout(() => onEditActivity(timelineItems.length), 0);
        }
    };

    const handleCreateNewSurvey = () => {
        const newItem = {
            id: `new_survey_${Date.now()}`,
            item_type: 'survey',
            trigger_day: 7,
            order: timelineItems.length,
            survey: {
                title: 'New Survey',
                description: '',
                is_anonymous: false,
                is_compliance_required: false,
                questions: [
                    { question_text: 'How would you rate your experience so far?', question_type: 'rating', is_required: true, options: [] }
                ]
            },
            assignment_placeholders: []
        };
        setTimelineItems(prev => [...prev, newItem]);
        setShowAddModal(false);
        toast.success('New survey created');
        if (onEditActivity) {
            setTimeout(() => onEditActivity(timelineItems.length), 0);
        }
    };

    // ─── Card Grid Helpers ───

    const getCardBadgeConfig = (item) => {
        if (item.task) {
            switch (item.task.task_type) {
                case 'quiz': return { label: 'Quiz', bg: 'bg-purple-100', text: 'text-purple-700', icon: <FileText className="w-3 h-3" /> };
                case 'training': return { label: 'Training', bg: 'bg-blue-100', text: 'text-blue-700', icon: <FileText className="w-3 h-3" /> };
                case 'checklist': return { label: 'Checklist', bg: 'bg-green-100', text: 'text-green-700', icon: <CheckSquare className="w-3 h-3" /> };
                default: return { label: 'Task', bg: 'bg-blue-100', text: 'text-blue-700', icon: <CheckSquare className="w-3 h-3" /> };
            }
        }
        if (item.notice) return { label: 'Notice', bg: 'bg-amber-100', text: 'text-amber-700', icon: <Bell className="w-3 h-3" /> };
        if (item.survey) return { label: 'Survey', bg: 'bg-purple-100', text: 'text-purple-700', icon: <MessageSquare className="w-3 h-3" /> };
        switch (item.item_type) {
            case 'task': return { label: 'Task', bg: 'bg-blue-100', text: 'text-blue-700', icon: <CheckSquare className="w-3 h-3" /> };
            case 'notice': return { label: 'Notice', bg: 'bg-amber-100', text: 'text-amber-700', icon: <Bell className="w-3 h-3" /> };
            case 'survey': return { label: 'Survey', bg: 'bg-purple-100', text: 'text-purple-700', icon: <MessageSquare className="w-3 h-3" /> };
            default: return { label: item.item_type || 'Item', bg: 'bg-secondary/50', text: 'text-foreground', icon: <FileText className="w-3 h-3" /> };
        }
    };

    const getCardTitle = (item) => {
        return item.task?.title || item.notice?.title || item.survey?.title || item.name || 'Untitled';
    };

    const getAssigneeLabel = (item) => {
        const placeholders = item.assignment_placeholders || item.task?.assignment_placeholders || item.notice?.assignment_placeholders || item.survey?.assignment_placeholders;
        if (!placeholders || placeholders.length === 0) return 'Employee';
        return placeholders.map(p => {
            const slug = typeof p === 'string' ? p : (p.placeholder_type === 'role' ? p.role_name : p.placeholder_type);
            if (!slug) return 'Employee';
            return slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        }).join(', ');
    };

    const getFeatureTags = (item) => {
        const tags = [];
        if (item.task?.requires_signature) tags.push({ label: 'E-sign', color: 'bg-pink-100 text-pink-700' });
        if (item.task?.requires_proof) tags.push({ label: 'Proof', color: 'bg-cyan-100 text-cyan-700' });
        if (item.task?.is_compliance_required || item.survey?.is_compliance_required)
            tags.push({ label: 'Compliance', color: 'bg-red-100 text-red-700' });
        if (item.survey?.is_anonymous) tags.push({ label: 'Anonymous', color: 'bg-secondary/50 text-muted-foreground' });
        return tags;
    };

    // Group timeline items by day for card rendering
    const itemsByDay = {};
    const sortedTimelineItems = [...timelineItems].sort((a, b) => (a.trigger_day || 0) - (b.trigger_day || 0));
    sortedTimelineItems.forEach(item => {
        const day = item.trigger_day || 0;
        if (!itemsByDay[day]) itemsByDay[day] = [];
        itemsByDay[day].push(item);
    });
    const timelineDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

    return (
        <div className="flex flex-col h-full bg-card">
            {/* Editor Header - Step Progress */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-secondary gap-3 sm:gap-0">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPreboarding ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                        <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-foreground">
                            {formData.name || 'Untitled Course'}
                        </h2>
                        <p className="text-xs text-muted-foreground capitalize">
                            {courseType} Course
                        </p>
                    </div>
                </div>

                {/* Step Progress Indicator */}
                <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto overflow-x-auto">
                    {[
                        { id: 'details', label: 'Details', step: 1 },
                        { id: 'timeline', label: 'Timeline', step: 2 },
                        { id: 'enrollment', label: 'Enrollment', step: 3 },
                        { id: 'review', label: 'Review', step: 4 }
                    ].map(({ id, label, step }, index) => {
                        const stepIndex = steps.indexOf(id);
                        const isCompleted = stepIndex < currentStepIndex;
                        const isCurrent = id === activeStep;

                        return (
                            <React.Fragment key={id}>
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${isCompleted
                                        ? 'bg-green-500 text-white'
                                        : isCurrent
                                            ? 'bg-primary text-white ring-4 ring-primary/20'
                                            : 'bg-muted text-muted-foreground'
                                        }`}>
                                        {isCompleted ? <Check className="w-4 h-4" /> : step}
                                    </div>
                                    <span className={`hidden sm:inline text-sm font-medium ${isCurrent ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'
                                        }`}>
                                        {label}
                                    </span>
                                </div>
                                {index < 3 && (
                                    <div className={`w-6 sm:w-10 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Editor Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Step 1: Details */}
                {activeStep === 'details' && (
                    <div className="space-y-5">
                        {/* Course Name */}
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">
                                Course Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={isPreboarding ? "e.g., New Hire Preboarding Journey" : "e.g., Engineering Onboarding Program"}
                                className="w-full px-4 py-3 border border-border/80 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-lg"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Briefly describe what this course covers..."
                                rows={3}
                                className="w-full px-4 py-3 border border-border/80 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Duration */}
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">
                                Duration (days)
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={formData.duration_days}
                                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 30 })}
                                    className="w-32 px-4 py-3 border border-border/80 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <span className="text-sm text-muted-foreground">Days from enrollment</span>
                            </div>
                            <div className="flex gap-2 mt-3">
                                {[7, 14, 30, 60, 90].map(days => (
                                    <button
                                        key={days}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, duration_days: days })}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${formData.duration_days === days
                                            ? 'bg-primary/10 text-primary border border-primary/30'
                                            : 'bg-secondary/50 text-muted-foreground hover:bg-muted'
                                            }`}
                                    >
                                        {days} days
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Timeline */}
                {activeStep === 'timeline' && (
                    <div className="space-y-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Course Timeline
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">Add tasks, notices, and surveys to be triggered during the course</p>
                            </div>
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Add Item
                            </button>
                        </div>

                        {/* Info Box */}
                        <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100 flex items-start gap-2">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <p>
                                Items are triggered relative to the enrollment date. Day 0 = enrollment day.
                                {isPreboarding ? ' Negative days trigger before start date.' : ' Positive days trigger after start.'}
                            </p>
                        </div>

                        {/* Timeline Items - Day Grouped Card Grid */}
                        {timelineItems.length === 0 ? (
                            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
                                <div className="w-16 h-16 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Calendar className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h4 className="font-medium text-foreground mb-2">No timeline items yet</h4>
                                <p className="text-sm text-muted-foreground mb-4">Add tasks, notices, or surveys to this course's timeline</p>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium text-sm hover:bg-primary/20 transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Your First Item
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {timelineDays.map((day) => (
                                    <div key={day}>
                                        {/* Day Header */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                                                Day {day}
                                            </span>
                                            <div className="flex-1 h-px bg-muted"></div>
                                            <span className="text-xs text-muted-foreground">
                                                {itemsByDay[day].length} {itemsByDay[day].length === 1 ? 'item' : 'items'}
                                            </span>
                                        </div>

                                        {/* Cards Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {itemsByDay[day].map((item) => {
                                                const originalIndex = timelineItems.findIndex(ti => ti.id === item.id);
                                                const badge = getCardBadgeConfig(item);
                                                const title = getCardTitle(item);
                                                const assignee = getAssigneeLabel(item);
                                                const tags = getFeatureTags(item);
                                                const isMenuOpen = openMenuId === item.id;
                                                const isInlineItem = item.task || item.notice || item.survey;

                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group relative"
                                                        onClick={() => {
                                                            if (isInlineItem && onEditActivity) {
                                                                onEditActivity(originalIndex);
                                                            }
                                                        }}
                                                    >
                                                        {/* Top: Badge + Kebab Menu */}
                                                        <div className="flex items-start justify-between mb-3">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
                                                                {badge.icon}
                                                                {badge.label}
                                                            </span>
                                                            <div className="relative">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenMenuId(isMenuOpen ? null : item.id);
                                                                    }}
                                                                    className="p-1.5 text-muted-foreground hover:text-muted-foreground hover:bg-secondary/50 rounded-lg transition-colors"
                                                                >
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </button>
                                                                {isMenuOpen && (
                                                                    <div className="absolute right-0 top-8 w-44 bg-card border border-border rounded-xl shadow-lg z-10 py-1">
                                                                        {isInlineItem && onEditActivity && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    onEditActivity(originalIndex);
                                                                                    setOpenMenuId(null);
                                                                                }}
                                                                                className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary flex items-center gap-2"
                                                                            >
                                                                                <PenLine className="w-3.5 h-3.5" />
                                                                                Edit Details
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setChangeDayItemId(item.id);
                                                                                setChangeDayValue(item.trigger_day);
                                                                                setOpenMenuId(null);
                                                                            }}
                                                                            className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary flex items-center gap-2"
                                                                        >
                                                                            <Calendar className="w-3.5 h-3.5" />
                                                                            Change Day
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveTimelineItem(item.id);
                                                                                setOpenMenuId(null);
                                                                            }}
                                                                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Title */}
                                                        <h4 className="font-semibold text-foreground mb-3 line-clamp-2 leading-snug">{title}</h4>

                                                        {/* Assignee */}
                                                        <div className="flex items-center gap-1.5 text-sm text-blue-600 mb-1.5">
                                                            <Users className="w-3.5 h-3.5" />
                                                            <span>{assignee}</span>
                                                        </div>

                                                        {/* Due Day */}
                                                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            <span>Due Day {item.trigger_day}</span>
                                                        </div>

                                                        {/* Feature Tags */}
                                                        {tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                                                                {tags.map(tag => (
                                                                    <span key={tag.label} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${tag.color}`}>
                                                                        {tag.label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Timeline Summary */}
                        {timelineItems.length > 0 && (
                            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                                <h4 className="font-medium text-foreground mb-3">Timeline Summary</h4>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                        {timelineItems.filter(i => i.item_type === 'task' || i.task).length} Tasks
                                    </span>
                                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                        {timelineItems.filter(i => i.item_type === 'notice' || i.notice).length} Notices
                                    </span>
                                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                        {timelineItems.filter(i => i.item_type === 'survey' || i.survey).length} Surveys
                                    </span>
                                    {timelineItems.some(i => (i.task || i.notice || i.survey) && !i.item_id) && (
                                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                                            {timelineItems.filter(i => (i.task || i.notice || i.survey) && !i.item_id).length} AI Generated
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 3: Enrollment */}
                {activeStep === 'enrollment' && (
                    <div className="space-y-5">
                        <div>
                            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Briefcase className="w-4 h-4" />
                                Enrollment Settings
                            </h3>

                            {/* Enrollment Method */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, enrollment_method: 'manual' })}
                                    className={`p-4 rounded-xl border-2 text-left transition-all ${formData.enrollment_method === 'manual'
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:border-border/80'
                                        }`}
                                >
                                    <div className="font-medium text-foreground mb-1">Manual Enrollment</div>
                                    <p className="text-xs text-muted-foreground">Assign employees manually to this course</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, enrollment_method: 'automatic' })}
                                    className={`p-4 rounded-xl border-2 text-left transition-all ${formData.enrollment_method === 'automatic'
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:border-border/80'
                                        }`}
                                >
                                    <div className="font-medium text-foreground mb-1">Automatic Enrollment</div>
                                    <p className="text-xs text-muted-foreground">Enroll based on rules (role, lifecycle, etc.)</p>
                                </button>
                            </div>

                            {/* Automation Rules */}
                            {formData.enrollment_method === 'automatic' && (
                                <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                                    <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100 flex items-start gap-2">
                                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        <p>Employees matching {formData.auto_enrollment_rules?.matching_logic === 'all' ? 'ALL' : 'ANY'} of the following criteria will be automatically enrolled.</p>
                                    </div>

                                    {/* Logic Selector */}
                                    <div className="flex bg-secondary/50 p-1 rounded-lg w-fit">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                auto_enrollment_rules: { ...prev.auto_enrollment_rules, matching_logic: 'any' }
                                            }))}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${formData.auto_enrollment_rules?.matching_logic !== 'all'
                                                ? 'bg-card shadow text-foreground'
                                                : 'text-muted-foreground hover:text-foreground'
                                                }`}
                                        >
                                            Match Any (OR)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                auto_enrollment_rules: { ...prev.auto_enrollment_rules, matching_logic: 'all' }
                                            }))}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${formData.auto_enrollment_rules?.matching_logic === 'all'
                                                ? 'bg-card shadow text-foreground'
                                                : 'text-muted-foreground hover:text-foreground'
                                                }`}
                                        >
                                            Match All (AND)
                                        </button>
                                    </div>

                                    {/* Triggers Accordion */}
                                    <div className="border border-border rounded-xl overflow-hidden divide-y divide-gray-100">
                                        {/* Lifecycle Stage Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('lifecycle')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-secondary transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-green-100 rounded-lg">
                                                        <Clock className="w-4 h-4 text-green-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Lifecycle Stage</span>
                                                        {formData.auto_enrollment_rules?.lifecycle_stages?.length > 0 && (
                                                            <p className="text-sm text-green-600">{formData.auto_enrollment_rules.lifecycle_stages.length} selected</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.lifecycle ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.lifecycle && (
                                                <div className="px-4 pb-4">
                                                    <div className="space-y-2">
                                                        {['onboarding', 'preboarding', 'probation', 'regular'].map((stage) => (
                                                            <label key={stage} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.auto_enrollment_rules?.lifecycle_stages?.includes(stage) || false}
                                                                    onChange={() => handleLifecycleChange(stage)}
                                                                    className="w-4 h-4 text-primary border-border/80 rounded focus:ring-primary"
                                                                />
                                                                <span className="text-sm text-foreground capitalize">{stage}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Roles Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('roles')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-secondary transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-blue-100 rounded-lg">
                                                        <Briefcase className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Job Roles</span>
                                                        {formData.auto_enrollment_rules?.roles?.length > 0 && (
                                                            <p className="text-sm text-blue-600">{formData.auto_enrollment_rules.roles.length} selected</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.roles ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.roles && (
                                                <div className="px-4 pb-4">
                                                    {loadingRoles ? (
                                                        <div className="flex items-center justify-center py-4">
                                                            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                                                        </div>
                                                    ) : availableRoles.length === 0 ? (
                                                        <p className="text-sm text-muted-foreground py-2">No roles found. Create roles in Settings.</p>
                                                    ) : (
                                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                                            {availableRoles.map((role) => {
                                                                const roleName = role.role_name || role.name;
                                                                return (
                                                                    <label key={role.id || roleName} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={formData.auto_enrollment_rules?.roles?.includes(roleName) || false}
                                                                            onChange={() => handleRoleToggle(roleName)}
                                                                            className="w-4 h-4 text-primary border-border/80 rounded focus:ring-primary"
                                                                        />
                                                                        <span className="text-sm text-foreground">{roleName}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Company-Wide Trigger */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection('companyWide')}
                                                className="w-full p-4 flex items-center justify-between hover:bg-secondary transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-purple-100 rounded-lg">
                                                        <Globe className="w-4 h-4 text-purple-600" />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="font-medium text-foreground">Company-Wide</span>
                                                        {formData.auto_enrollment_rules?.company_wide && (
                                                            <p className="text-sm text-purple-600">Enabled - All employees</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedSections.companyWide ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                            </button>
                                            {expandedSections.companyWide && (
                                                <div className="px-4 pb-4">
                                                    <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-yellow-200 bg-yellow-50 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.auto_enrollment_rules?.company_wide || false}
                                                            onChange={(e) => handleCompanyWide(e.target.checked)}
                                                            className="mt-1 w-4 h-4 text-primary border-border/80 rounded focus:ring-primary"
                                                        />
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-foreground">Enroll all employees</span>
                                                                <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs font-semibold rounded">
                                                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                                    Warning
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                This will enroll everyone in the company when they match the lifecycle stage.
                                                            </p>
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Enrollment Preview */}
                                    {hasAnyTriggers && (
                                        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium text-foreground">Enrollment Preview</h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {previewLoading ? (
                                                            <span className="flex items-center gap-1">
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                                Calculating...
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span className="font-bold text-primary">{previewCount}</span> existing employees will be enrolled immediately
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPreviewModal(true)}
                                                    disabled={previewLoading || previewCount === 0}
                                                    className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    Preview List
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 4: Review */}
                {activeStep === 'review' && (
                    <div className="space-y-5">
                        <h3 className="text-base font-semibold text-foreground mb-3">Review Course Settings</h3>

                        <div className="bg-secondary rounded-xl p-5 space-y-4">
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Course Name</span>
                                <span className="text-sm font-medium text-foreground text-right">{formData.name || 'Not set'}</span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Description</span>
                                <span className="text-sm font-medium text-foreground text-right max-w-[60%]">{formData.description || 'No description'}</span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Duration</span>
                                <span className="text-sm font-medium text-foreground">{formData.duration_days} days</span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Course Type</span>
                                <span className={`text-sm font-medium capitalize ${isPreboarding ? 'text-purple-600' : 'text-blue-600'}`}>{courseType}</span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Timeline Items</span>
                                <span className="text-sm font-medium text-foreground">{timelineItems.length} items</span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-sm text-muted-foreground">Enrollment Method</span>
                                <span className="text-sm font-medium text-foreground capitalize">{formData.enrollment_method}</span>
                            </div>
                            {formData.enrollment_method === 'automatic' && (
                                <>
                                    <div className="flex justify-between items-start">
                                        <span className="text-sm text-muted-foreground">Matching Logic</span>
                                        <span className="text-sm font-medium text-foreground">{formData.auto_enrollment_rules?.matching_logic === 'all' ? 'Match All (AND)' : 'Match Any (OR)'}</span>
                                    </div>
                                    {formData.auto_enrollment_rules?.lifecycle_stages?.length > 0 && (
                                        <div className="flex justify-between items-start">
                                            <span className="text-sm text-muted-foreground">Lifecycle Stages</span>
                                            <span className="text-sm font-medium text-foreground capitalize">{formData.auto_enrollment_rules.lifecycle_stages.join(', ')}</span>
                                        </div>
                                    )}
                                    {formData.auto_enrollment_rules?.roles?.length > 0 && (
                                        <div className="flex justify-between items-start">
                                            <span className="text-sm text-muted-foreground">Roles</span>
                                            <span className="text-sm font-medium text-foreground">{formData.auto_enrollment_rules.roles.join(', ')}</span>
                                        </div>
                                    )}
                                    {formData.auto_enrollment_rules?.company_wide && (
                                        <div className="flex justify-between items-start">
                                            <span className="text-sm text-muted-foreground">Company-Wide</span>
                                            <span className="text-sm font-medium text-yellow-600">Yes - All employees</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {!formData.name?.trim() && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Course name is required to save.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer Navigation */}
            <div className="border-t border-border bg-secondary px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground justify-center sm:justify-start">
                        {isLastStep ? (
                            formData.name?.trim() ? (
                                <>
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    Ready to create
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    Add a name to save
                                </>
                            )
                        ) : (
                            <span className="text-muted-foreground">Step {currentStepIndex + 1} of {steps.length}</span>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3">
                        <button
                            onClick={handleCancelWithConfirmation}
                            className="px-3 sm:px-4 py-2 text-foreground hover:bg-secondary/50 rounded-lg transition-colors text-sm"
                        >
                            Cancel
                        </button>

                        {!isFirstStep && (
                            <button
                                onClick={handlePrevStep}
                                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-border/80 text-foreground rounded-lg hover:bg-secondary/50 transition-colors text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}

                        {!isLastStep ? (
                            <button
                                onClick={handleNextStep}
                                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors text-sm"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => onSave('draft')}
                                    disabled={isSaving || !formData.name?.trim()}
                                    className="flex items-center gap-2 px-4 py-2 border border-primary/20 text-primary hover:bg-primary/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                >
                                    <Save className="w-4 h-4" />
                                    Save as Draft
                                </button>
                                <button
                                    onClick={() => onSave('published')}
                                    disabled={isSaving || !formData.name?.trim()}
                                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-lg shadow-violet-500/25"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            Create Course
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Change Day Modal */}
            {changeDayItemId && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60]" onClick={() => setChangeDayItemId(null)}>
                    <div className="bg-card rounded-xl shadow-xl p-6 w-80" onClick={e => e.stopPropagation()}>
                        <h3 className="font-semibold text-foreground mb-4">Change Trigger Day</h3>
                        <input
                            type="number"
                            value={changeDayValue}
                            onChange={(e) => setChangeDayValue(e.target.value)}
                            min={isPreboarding ? -30 : 0}
                            max={formData.duration_days}
                            className="w-full px-3 py-2 border border-border/80 rounded-lg text-sm focus:ring-2 focus:ring-primary mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setChangeDayItemId(null)} className="px-4 py-2 text-sm text-muted-foreground hover:bg-secondary/50 rounded-lg">Cancel</button>
                            <button
                                onClick={() => {
                                    handleUpdateTriggerDay(changeDayItemId, changeDayValue);
                                    setChangeDayItemId(null);
                                }}
                                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Item Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-0 md:p-4">
                    <div className="bg-card w-full h-full md:rounded-xl md:shadow-2xl md:max-w-2xl md:w-full md:h-auto md:max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="px-4 py-3 md:px-5 md:py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-foreground">Add Item to Timeline</h3>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="p-1.5 text-muted-foreground hover:bg-secondary/50 rounded-lg"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Item Type Tabs */}
                        <div className="flex border-b border-border">
                            {[
                                { id: 'task', label: 'Tasks', icon: CheckSquare, count: availableTasks.length },
                                { id: 'notice', label: 'Notices', icon: Bell, count: availableNotices.length },
                                { id: 'survey', label: 'Surveys', icon: MessageSquare, count: availableSurveys.length }
                            ].map(({ id, label, icon: Icon, count }) => (
                                <button
                                    key={id}
                                    onClick={() => setAddItemType(id)}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium text-sm transition-colors ${addItemType === id
                                        ? 'text-primary border-b-2 border-violet-600 bg-primary/5'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                    <span className="px-1.5 py-0.5 bg-muted text-muted-foreground text-xs rounded">{count}</span>
                                </button>
                            ))}
                        </div>

                        {/* Item List */}
                        <div className="p-4 overflow-y-auto flex-1 md:max-h-[calc(80vh-180px)]">
                            {/* Create New Task Section */}
                            {addItemType === 'task' && (
                                <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-primary/20 rounded-xl">
                                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                                        <Plus className="w-4 h-4 text-primary" />
                                        Create New Task
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-3">Create a new task inline without leaving the course editor</p>
                                    <div className="flex flex-wrap gap-2">
                                        <button onClick={() => handleCreateNewTask('quiz')} className="flex items-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium transition-colors">
                                            <FileText className="w-4 h-4" /> Quiz
                                        </button>
                                        <button onClick={() => handleCreateNewTask('training')} className="flex items-center gap-2 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors">
                                            <FileText className="w-4 h-4" /> Training
                                        </button>
                                        <button onClick={() => handleCreateNewTask('checklist')} className="flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors">
                                            <CheckSquare className="w-4 h-4" /> Checklist
                                        </button>
                                        <button onClick={() => handleCreateNewTask('task')} className="flex items-center gap-2 px-3 py-2 bg-secondary/50 hover:bg-muted text-foreground rounded-lg text-sm font-medium transition-colors">
                                            <CheckSquare className="w-4 h-4" /> Basic Task
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Create New Notice Section */}
                            {addItemType === 'notice' && (
                                <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                                        <Plus className="w-4 h-4 text-amber-600" />
                                        Create New Notice
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-3">Create a notice inline without leaving the course editor</p>
                                    <button onClick={handleCreateNewNotice} className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition-colors">
                                        <Bell className="w-4 h-4" /> Create Notice
                                    </button>
                                </div>
                            )}

                            {/* Create New Survey Section */}
                            {addItemType === 'survey' && (
                                <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
                                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                                        <Plus className="w-4 h-4 text-purple-600" />
                                        Create New Survey
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-3">Create a feedback survey inline without leaving the course editor</p>
                                    <button onClick={handleCreateNewSurvey} className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors">
                                        <MessageSquare className="w-4 h-4" /> Create Survey
                                    </button>
                                </div>
                            )}

                            {/* Existing Items Section */}
                            {loadingItems ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {addItemType === 'task' && availableTasks.length > 0 && (
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                                            Or add existing tasks
                                        </p>
                                    )}
                                    {(addItemType === 'task' ? availableTasks :
                                        addItemType === 'notice' ? availableNotices :
                                            availableSurveys).map((item) => {
                                                const isAlreadyAdded = timelineItems.some(ti =>
                                                    ti.item_id === item.id && ti.item_type === addItemType
                                                );
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isAlreadyAdded
                                                            ? 'bg-secondary border-border opacity-50'
                                                            : 'bg-card border-border hover:border-primary/30'
                                                            }`}
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-medium text-foreground truncate">
                                                                {item.title || item.name || item.subject}
                                                            </h4>
                                                            <p className="text-xs text-muted-foreground truncate">
                                                                {item.description?.substring(0, 100) || 'No description'}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleAddTimelineItem(item, addItemType)}
                                                            disabled={isAlreadyAdded}
                                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isAlreadyAdded
                                                                ? 'bg-secondary/50 text-muted-foreground cursor-not-allowed'
                                                                : 'bg-primary/10 text-primary hover:bg-primary/20'
                                                                }`}
                                                        >
                                                            {isAlreadyAdded ? (
                                                                <><Check className="w-3 h-3" /> Added</>
                                                            ) : (
                                                                <><Plus className="w-3 h-3" /> Add</>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                </div>
                            )}
                        </div>

                        <div className="px-4 py-3 md:px-5 border-t border-gray-100 bg-secondary">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="w-full py-2 bg-muted hover:bg-gray-300 text-foreground rounded-lg font-medium transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-0 md:p-4">
                    <div className="bg-card w-full h-full md:rounded-xl md:shadow-2xl md:max-w-md md:w-full md:h-auto md:max-h-[70vh] overflow-hidden flex flex-col">
                        <div className="px-4 py-3 md:px-5 md:py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-foreground">Employees to Enroll ({previewCount})</h3>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="p-1.5 text-muted-foreground hover:bg-secondary/50 rounded-lg"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 md:max-h-[calc(70vh-120px)]">
                            {previewEmployees.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No employees match current criteria</p>
                            ) : (
                                <div className="space-y-2">
                                    {previewEmployees.map((emp) => (
                                        <div key={emp.id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground text-sm">{emp.first_name} {emp.last_name}</p>
                                                <p className="text-xs text-muted-foreground">{emp.position || emp.role_name || 'No role'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="px-4 py-3 md:px-5 border-t border-gray-100 bg-secondary">
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="w-full py-2 bg-muted hover:bg-gray-300 text-foreground rounded-lg font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseEditor;
